import React, { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCampaign } from "./CampaignContext";
import { buildTimelineData } from "../CampaignSetup/TimelineDisplay";
import settlementEvents from "../All Cards/SettlementEvents";

function ensureEntry(tp, key) {
  const entry = tp?.[key] || {};
  return {
    yearComplete: Boolean(entry.yearComplete),
    checkedEvents:
      entry.checkedEvents && typeof entry.checkedEvents === "object"
        ? entry.checkedEvents
        : {},
    foughtMonster: entry.foughtMonster || "",
    foughtLevel: entry.foughtLevel || "",
    notes: entry.notes || "",
    settlementEvent: entry.settlementEvent || "",
  };
}

function defaultSEForYear1(campaignType) {
  if (campaignType === "People of the Dream Keeper") return "First Crimson Day";
  if (
    campaignType === "People of the Lantern" ||
    campaignType === "People of the Stars" ||
    campaignType === "People of the Sun"
  )
    return "First Day";
  return "";
}

// Core / Finale monsters have no level (for now)
const CORE_OR_FINALE_NO_LEVEL = new Set([
  "Watcher",
  "Gambler",
  "Gold Smoke Knight",
  "Godhand",
]);

function EventCheckboxList({ events, checkedEvents, onToggle }) {
  if (!events || events.length === 0) return <div style={{ opacity: 0.75 }}>No events</div>;
  return (
    <div style={{ marginTop: 8 }}>
      {events.map((t) => (
        <label key={t} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
          <input
            type="checkbox"
            checked={Boolean(checkedEvents?.[t])}
            onChange={() => onToggle(t)}
          />
          <span>{t}</span>
        </label>
      ))}
    </div>
  );
}

function YearTrackerBlock({
  title,
  events,
  entry,
  onUpdate,
  showFightFields,
  showSettlementEventPicker,
  settlementEventOptions,
  foughtMonsterOptions,
}) {
  const toggleEvent = (text) => {
    const next = { ...(entry.checkedEvents || {}) };
    next[text] = !Boolean(next[text]);
    onUpdate({ checkedEvents: next });
  };

  const onChangeSettlementEvent = (val) => {
    if (val === "__RANDOM__") {
      if (!settlementEventOptions.length) return;
      const idx = Math.floor(Math.random() * settlementEventOptions.length);
      onUpdate({ settlementEvent: settlementEventOptions[idx] });
      return;
    }
    onUpdate({ settlementEvent: val });
  };

  const foughtMonsterHasNoLevel = CORE_OR_FINALE_NO_LEVEL.has(entry.foughtMonster);

  const onChangeFoughtMonster = (val) => {
    // If user selects a no-level monster, force level to None
    if (CORE_OR_FINALE_NO_LEVEL.has(val)) {
      onUpdate({ foughtMonster: val, foughtLevel: "" });
    } else {
      onUpdate({ foughtMonster: val });
    }
  };

  // NEW: White Lion + Crimson Crocodile allow level 0
  const levelAllowsZero =
    entry.foughtMonster === "White Lion" || entry.foughtMonster === "Crimson Crocodile";

  return (
    <div style={{ border: "1px solid #ddd", padding: 12, marginBottom: 14, maxWidth: 980 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <input
          type="checkbox"
          checked={entry.yearComplete}
          onChange={(e) => onUpdate({ yearComplete: e.target.checked })}
          title="Mark complete"
        />
        <div style={{ fontWeight: 800, fontSize: 16 }}>{title}</div>

        {showSettlementEventPicker ? (
          <>
            <img
              src="/assets/settlementEvent.jpeg"
              alt="Settlement Event"
              style={{ height: 44, width: "auto", marginLeft: 6 }}
            />

            <select
              value={entry.settlementEvent}
              onChange={(e) => onChangeSettlementEvent(e.target.value)}
              style={{ padding: 6, minWidth: 240 }}
              title="Settlement Event"
            >
              <option value="__RANDOM__">Random Event</option>
              <option value="">-- Select Settlement Event --</option>
              {settlementEventOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </>
        ) : null}
      </div>

      <EventCheckboxList events={events} checkedEvents={entry.checkedEvents} onToggle={toggleEvent} />

      <div style={{ marginTop: 12, borderTop: "1px solid #eee", paddingTop: 12 }}>
        {showFightFields ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 700 }}>Fought this year:</div>

            {/* Monster dropdown (from campaign monsters) */}
            <select
              value={entry.foughtMonster}
              onChange={(e) => onChangeFoughtMonster(e.target.value)}
              style={{ padding: 6, minWidth: 260 }}
              title="Monster fought"
            >
              <option value="">-- Select Monster --</option>
              {foughtMonsterOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>

            <div style={{ fontWeight: 700 }}>Level:</div>
            <select
              value={entry.foughtLevel}
              onChange={(e) => onUpdate({ foughtLevel: e.target.value })}
              style={{ padding: 6 }}
              disabled={foughtMonsterHasNoLevel || !entry.foughtMonster}
              title={foughtMonsterHasNoLevel ? "Core/Finale monsters have no level" : "Monster level"}
            >
              <option value="">None</option>

              {levelAllowsZero ? <option value="0">0</option> : null}

              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
            </select>
          </div>
        ) : null}

        <div style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Notes:</div>
          <textarea
            value={entry.notes}
            onChange={(e) => onUpdate({ notes: e.target.value })}
            placeholder="Notes..."
            style={{ width: "100%", minHeight: 80, padding: 8 }}
          />
        </div>
      </div>
    </div>
  );
}

export default function TimelinePage() {
  const nav = useNavigate();
  const { current, updateTimelineProgress } = useCampaign();

  const hasCampaign = Boolean(current?.settlementName?.trim());
  if (!hasCampaign) {
    return (
      <div style={{ padding: 16 }}>
        <h2>Timeline</h2>
        <div style={{ opacity: 0.85 }}>No campaign loaded.</div>
        <div style={{ marginTop: 12 }}>
          <button onClick={() => nav("/")}>Go Home</button>
        </div>
      </div>
    );
  }

  const selectedCampaign = current.campaignType || "";
  const selectedMonsters = current.monsters || [];
  const selectedPillars = current.pillars || [];

  const { prologueMonster, prologueEvents, years } = buildTimelineData({
    selectedCampaign,
    selectedMonsters,
    selectedPillars,
    yearsCount: 39,
  });

  const tp = current.timelineProgress || {};

  const settlementEventOptions = useMemo(() => {
    const arr = Array.isArray(settlementEvents) ? settlementEvents : [];
    return arr
      .map((e) => String(e?.name || "").trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, []);

  const foughtMonsterOptions = useMemo(() => {
    const arr = Array.isArray(current?.monsters) ? current.monsters : [];
    const cleaned = arr.map((m) => String(m || "").trim()).filter(Boolean);
    return Array.from(new Set(cleaned)).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [current?.monsters]);

  // Apply Year 1 default settlement event once if not already set
  useEffect(() => {
    const key = "year_1";
    const existing = (current?.timelineProgress?.[key]?.settlementEvent || "").trim();
    if (existing) return;

    const def = defaultSEForYear1(selectedCampaign);
    if (!def) return;

    updateTimelineProgress(key, { settlementEvent: def });
  }, [selectedCampaign, current?.timelineProgress, updateTimelineProgress]);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ margin: 0 }}>Timeline</h2>
        <button style={{ marginLeft: "auto" }} onClick={() => nav("/settlement")}>
          Back to Settlement
        </button>
      </div>

      <div style={{ marginTop: 8, opacity: 0.85 }}>
        Settlement: <b>{current.settlementName}</b> — Campaign:{" "}
        <b>{selectedCampaign || "-"}</b>
      </div>

      <div style={{ marginTop: 14 }}>
        {/* Prologue (no SE dropdown) */}
        <YearTrackerBlock
          title={`Prologue: ${prologueMonster}`}
          events={prologueEvents}
          entry={ensureEntry(tp, "prologue")}
          onUpdate={(patch) => updateTimelineProgress("prologue", patch)}
          showFightFields={true}
          showSettlementEventPicker={false}
          settlementEventOptions={settlementEventOptions}
          foughtMonsterOptions={foughtMonsterOptions}
        />

        {/* Years */}
        {years.map((y) => {
          const key = `year_${y.year}`;
          const baseEntry = ensureEntry(tp, key);

          const effectiveEntry =
            y.year === 1 && !baseEntry.settlementEvent
              ? { ...baseEntry, settlementEvent: defaultSEForYear1(selectedCampaign) }
              : baseEntry;

          return (
            <YearTrackerBlock
              key={key}
              title={`Year ${y.year}`}
              events={y.events}
              entry={effectiveEntry}
              onUpdate={(patch) => updateTimelineProgress(key, patch)}
              showFightFields={true}
              showSettlementEventPicker={true}
              settlementEventOptions={settlementEventOptions}
              foughtMonsterOptions={foughtMonsterOptions}
            />
          );
        })}
      </div>
    </div>
  );
}